import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Hash, AlertTriangle, RefreshCw, Save, Trash2, Search } from "lucide-react";

const LABELS = {
  factura_electronica: "Factura electrónica",
  nota_credito: "Nota de crédito",
  guia_remision: "Guía de remisión",
  retencion: "Retención SRI",
  nota_debito: "Nota de débito",
  liquidacion_compra: "Liquidación de compra",
};

export default function AdminSecuenciales() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, seq, newSeq }
  const [saving, setSaving] = useState(false);

  // Anulación retención
  const [retNumero, setRetNumero] = useState("");
  const [anulConfirm, setAnulConfirm] = useState(null); // { numero }
  const [anulando, setAnulando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/seq");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!editing) return;
    const newVal = parseInt(editing.newSeq);
    if (isNaN(newVal) || newVal < 0) return toast.error("Valor inválido");
    if (!window.confirm(
      `¿Confirma fijar el secuencial '${LABELS[editing.id] || editing.id}' en ${newVal}?\n\nEl PRÓXIMO documento generado tendrá número ${newVal + 1}.\n\nEsta acción es irreversible y afecta toda la numeración SRI.`
    )) return;
    try {
      setSaving(true);
      await api.post("/admin/seq/set", { _id: editing.id, seq: newVal });
      toast.success(`Secuencia actualizada. Próximo número: ${newVal + 1}`);
      setEditing(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const anularRetencion = async () => {
    if (!anulConfirm) return;
    try {
      setAnulando(true);
      const { data } = await api.delete(`/admin/seq/retencion/${encodeURIComponent(anulConfirm.numero)}`);
      toast.success(
        `Retención ${data.numero_anulado} anulada · Factura desenlazada${data.factura_compra_id ? ` (${data.factura_compra_id.slice(0, 8)}…)` : ""}`,
        { duration: 8000 }
      );
      setAnulConfirm(null);
      setRetNumero("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnulando(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-secuenciales-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Administración</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          <Hash className="inline mr-2 -mt-1" size={28} />
          Secuenciales SRI
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">
          Controle los contadores de numeración para facturas, retenciones y demás comprobantes SRI.
          También puede anular una retención fallida para liberar su número.
        </p>
      </div>

      <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-sm flex items-start gap-2">
        <AlertTriangle size={16} className="text-[#8a5500] mt-0.5 flex-shrink-0" />
        <div className="text-[#8a5500]">
          <strong>Advertencia:</strong> modificar un secuencial afecta la numeración de TODOS los próximos comprobantes.
          Use esta herramienta sólo cuando el SRI rechazó un documento y necesita liberar/reservar números.
          El <strong>próximo documento</strong> tendrá el número <code>seq + 1</code>.
        </div>
      </div>

      {/* Tabla de secuenciales */}
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b border-[#d1dbd6]">
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Contadores activos</div>
          <Button size="sm" variant="outline" onClick={cargar} disabled={loading} data-testid="reload-btn">
            <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} /> Recargar
          </Button>
        </div>
        <table className="erp-table">
          <thead>
            <tr>
              <th>Documento</th>
              <th className="w-40 text-right">Último usado</th>
              <th className="w-40 text-right">Próximo número</th>
              <th className="w-32 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="py-10 text-center text-[#8ba397]">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="py-10 text-center text-[#8ba397]">Aún no hay secuenciales creados</td></tr>
            ) : (
              items.map(it => (
                <tr key={it.id} data-testid={`seq-row-${it.id}`}>
                  <td className="font-semibold">{LABELS[it.id] || it.id}</td>
                  <td className="text-right tabular-nums font-mono text-base">{it.seq}</td>
                  <td className="text-right tabular-nums font-bold text-base text-[#205c45]">{(it.seq || 0) + 1}</td>
                  <td className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ id: it.id, seq: it.seq, newSeq: it.seq })} data-testid={`edit-${it.id}`}>
                      Editar
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Anular retención por número */}
      <div className="rounded-md border border-[#d1dbd6] bg-white p-4 space-y-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-1">Anular retención por número</div>
          <p className="text-sm text-[#4a5c53]">
            Si una retención SRI quedó en error/rechazada y necesita rehacer la factura con un nuevo número,
            búsquela aquí y anúlela. Se desenlazará de la factura de compra (puede regenerar la retención normalmente).
            <strong className="text-[#b53a25]"> No se puede anular si está AUTORIZADA por el SRI.</strong>
          </p>
        </div>
        <div className="flex gap-2 max-w-xl">
          <Input
            value={retNumero}
            onChange={(e) => setRetNumero(e.target.value)}
            placeholder="001-001-000002993 (o solo el número 2993)"
            data-testid="ret-numero-input"
          />
          <Button
            onClick={() => retNumero.trim() && setAnulConfirm({ numero: retNumero.trim() })}
            disabled={!retNumero.trim()}
            className="bg-[#b53a25] hover:bg-[#8f2a18]"
            data-testid="search-ret-btn"
          >
            <Search size={14} className="mr-1" /> Anular
          </Button>
        </div>
      </div>

      {/* Dialog editar secuencia */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar secuencial: {editing && (LABELS[editing.id] || editing.id)}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3">
                  <div className="text-xs uppercase tracking-wider text-[#5c7368]">Valor actual</div>
                  <div className="text-2xl font-bold tabular-nums">{editing.seq}</div>
                  <div className="text-[11px] text-[#5c7368]">Último usado</div>
                </div>
                <div className="rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-3">
                  <div className="text-xs uppercase tracking-wider text-[#5c7368]">Será</div>
                  <div className="text-2xl font-bold tabular-nums text-[#205c45]">
                    {(parseInt(editing.newSeq) || 0) + 1}
                  </div>
                  <div className="text-[11px] text-[#5c7368]">Próximo número emitido</div>
                </div>
              </div>
              <div>
                <Label>Nuevo valor del contador (último usado)</Label>
                <Input
                  type="number"
                  min="0"
                  value={editing.newSeq}
                  onChange={(e) => setEditing({ ...editing, newSeq: e.target.value })}
                  className="text-lg h-11 tabular-nums"
                  data-testid="new-seq-input"
                />
                <p className="text-[11px] text-[#5c7368] italic mt-1">
                  El próximo documento tendrá el número <strong>{(parseInt(editing.newSeq) || 0) + 1}</strong>.
                </p>
              </div>
              <Badge variant="outline" className="border-[#f5c477] text-[#8a5500] bg-[#fef7ec]">
                Esta acción se audita.
              </Badge>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={guardar} disabled={saving} className="bg-[#205c45]" data-testid="save-seq-btn">
              <Save size={14} className="mr-2" />
              {saving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmar anulación retención */}
      <Dialog open={!!anulConfirm} onOpenChange={(o) => !o && setAnulConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anular retención {anulConfirm?.numero}</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-2">
            <p>Se ejecutará:</p>
            <ul className="list-disc list-inside text-[#4a5c53] space-y-1">
              <li>Eliminar el documento de retención en la base de datos.</li>
              <li>Desenlazar la factura de compra asociada (queda lista para generar una retención nueva).</li>
              <li>Quedará registrado en auditoría con su usuario.</li>
            </ul>
            <p className="text-[#b53a25] font-semibold pt-2">
              ⚠️ No se ejecutará si la retención ya fue AUTORIZADA por el SRI.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnulConfirm(null)} disabled={anulando}>Cancelar</Button>
            <Button onClick={anularRetencion} disabled={anulando} className="bg-[#b53a25] hover:bg-[#8f2a18]" data-testid="confirm-anular-btn">
              <Trash2 size={14} className="mr-2" />
              {anulando ? "Anulando…" : "Sí, anular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
